/**
 * The language tag `Intl` writes a number with, so the decimal separator is the one the reader
 * uses: a comma in French, Portuguese or German, a point in English or Japanese.
 *
 * Two things are settled here and nowhere else. The app names its languages the i18next way
 * (`fr_FR`, `pt_BR`), which `Intl` rejects outright with a `RangeError`, so the underscore becomes
 * the hyphen of a language tag. And a language that carries no region of its own is read the
 * Canadian way, which is what this product has always done for French and English; `Intl` falls
 * back to the plain language wherever it has no Canadian data, so `pt-CA` reads as `pt`.
 */
export const toReaderLocale = (language: string): string => {
    const tag = language.replaceAll('_', '-');
    return tag.includes('-') ? tag : `${tag}-CA`;
};

export interface FractionDigitsOptions {
    /** Exactly this many decimals, trailing zeros included. */
    fractionDigits?: number;
}

const exactFractionDigits = (fractionDigits: number): Intl.NumberFormatOptions => ({
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
});

/**
 * Whole Canadian dollars, with the narrow `$` both official languages read: `65 000 $` in French,
 * `$65,000` in English.
 */
export const formatCurrency = (value: number, language: string): string =>
    new Intl.NumberFormat(toReaderLocale(language), {
        style: 'currency',
        currency: 'CAD',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
    }).format(value);

/**
 * A percentage (`6.38` for 6.38 %, never a 0-1 ratio) with its sign written the language's way:
 * `6,38 %` in French, `6.38%` in English. Without `fractionDigits`, up to two decimals and none on a
 * whole number.
 */
export const formatPercent = (
    value: number,
    language: string,
    { fractionDigits }: FractionDigitsOptions = {},
): string =>
    new Intl.NumberFormat(toReaderLocale(language), {
        style: 'percent',
        ...(fractionDigits === undefined
            ? { maximumFractionDigits: 2 }
            : exactFractionDigits(fractionDigits)),
    }).format(value / 100);

/**
 * A quantity somebody typed, read back to them: at most two decimals, none on a whole number, and
 * no digit grouping — `12,5` in French, `12.5` in English, `50000` in both. Grouping is left out
 * because these values sit next to the field they were entered in, and a space inside the number
 * reads as two numbers.
 */
export const formatQuantity = (value: number, language: string): string =>
    new Intl.NumberFormat(toReaderLocale(language), {
        maximumFractionDigits: 2,
        useGrouping: false,
    }).format(value);

/** A bare number with a fixed count of decimals (one by default): `2,3` in French, `2.3` in English. */
export const formatDecimal = (
    value: number,
    language: string,
    { fractionDigits = 1 }: FractionDigitsOptions = {},
): string =>
    new Intl.NumberFormat(toReaderLocale(language), exactFractionDigits(fractionDigits)).format(
        value,
    );

const separators = (language: string) => {
    const parts = new Intl.NumberFormat(toReaderLocale(language)).formatToParts(12345.5);
    const symbol = (type: Intl.NumberFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value;

    return { decimal: symbol('decimal') ?? '.', group: symbol('group') };
};

const DECIMAL_NUMBER = /^-?(\d+(\.\d*)?|\.\d+)$/;

/**
 * The digits of a number in plain notation. `String` keeps every decimal but writes `1e+21` from
 * 10^21 up and `1e-7` below 10^-6; `Intl` only takes over there, since it would round every other
 * value to the binary fraction it really holds (`12345.678` becomes `12345.67799999999988`).
 */
const plainDigits = (value: number): string => {
    const shortest = String(value);
    return shortest.includes('e')
        ? value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
        : shortest;
};

/**
 * A number laid out for editing in a text field: every decimal kept, no digit grouping, never an
 * exponent, and the decimal separator of the language — `82,5` in French, `82.5` in English. A value
 * that is not a finite number, such as a row whose values have not arrived yet, leaves the field
 * empty.
 */
export const formatEditableNumber = (value: number | null | undefined, language: string): string =>
    typeof value === 'number' && Number.isFinite(value)
        ? plainDigits(value).replace('.', separators(language).decimal)
        : '';

/**
 * Reads a number typed in a text field, `null` for text that is not one. The decimal separator of
 * the language is read, and so is a point wherever the language does not group digits with it,
 * since a keyboard set to another language offers only its own. A digit group is refused rather
 * than guessed: `82,5` in English is not 825, and `1.000` in German is not 1.
 */
export const parseEditableNumber = (text: string, language: string): number | null => {
    const { decimal, group } = separators(language);
    const trimmed = text.trim();
    if (group !== undefined && trimmed.includes(group)) {
        return null;
    }

    const normalized = trimmed.replaceAll(decimal, '.');
    return DECIMAL_NUMBER.test(normalized) ? Number(normalized) : null;
};
